import { ReactElement, useEffect, useMemo, useState } from 'react'
import ModifyBookingTemplate from './template'
import { moveBookLoadMoreDays, useLoadData } from './loadData'
import { useBFSM } from '../../contexts/bfsmContext'
import { useBFSMValue } from '../../services/hooks/useBFSMValues'
import {
    GeneralActions,
    MoveBookingActions,
    MoveBookingState,
} from '@raskrask-dk/bookingflow-state-machine'
import {
    IMoveBookingDTO,
    ModifyBookPartnerDetails,
    RRDate,
} from '@raskrask-dk/raskrask-utils'
import { InitialLoading } from '../chemistry/calendarComponents/loading'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
    getBookingId,
    getIsLoading,
    setErrorcase,
} from '../../redux/reducers/appSettings'
import { getCaruselDates } from '../calendar/connectModels'
import { BasisTypeBF } from '../../models'
import { MoveBookModalProps } from '../chemistry/modifybookComponents/movebook.components'
import { useEndFlowManager } from '../../contexts/endFlowManagerContext'

const massageBasisType = new BasisTypeBF({
    disabled: false,
    icon: '',
    id: 1,
    name: 'Massage',
})

export const ModifyBookingPage = (): ReactElement => {
    const [BFSM, , adminSettings] = useBFSM()
    const [EFM] = useEndFlowManager()
    const flowType = BFSM.flowType
    const dispatch = useAppDispatch()
    const bookingId = useAppSelector((state) => getBookingId(state))
    const isLoading = useAppSelector((state) => getIsLoading(state))
    const [antiSpam, setAntiSpam] = useState(false)

    // This 'fallBackPartner' is used to idsplay the PartnerPicture and Reviews, when the given Partner is not returned in the dependencies.
    const [fallBackPartner, setFallBackPartner] = useState<
        ModifyBookPartnerDetails | undefined
    >(undefined)

    const [numberOfDaysLoaded, setNumberOfDaysLoaded] = useState(28)
    const slideLength = 14
    const loadingStartDate = new RRDate()

    const [dependencies, setDependencies] = useBFSMValue<
        MoveBookingState,
        MoveBookingActions.SetDependencies,
        'dependencies'
    >('dependencies', MoveBookingActions.SetDependencies)

    const [shouldPayCancellationFee, setShouldPayCancellationFee] =
        useState<boolean>(false)
    const [numberOfHoursUntillServiceDate, setNumberOfHoursUntillServiceDate] =
        useState<number>(30)
    const [showCancelBookingModal, setShowCancelBookingModal] = useState(false)
    const [showMoveBookingModal, setShowMoveBookingModal] = useState(false)
    const [date, setDate] = useBFSMValue<
        MoveBookingState,
        MoveBookingActions.SetDate,
        'date'
    >('date', MoveBookingActions.SetDate)

    const [time, setTime] = useBFSMValue<
        MoveBookingState,
        MoveBookingActions.SetTime,
        'time'
    >('time', MoveBookingActions.SetTime)
    const canBookingBeMoved = numberOfHoursUntillServiceDate > 24
    const shouldShowCancelButton = flowType === 'CancelBooking'

    //bookingId must be a string, otherwise we are not loading this state
    const oldBooking = useLoadData(
        bookingId!,
        BFSM,
        setDependencies,
        numberOfDaysLoaded,
        loadingStartDate,
        flowType,
        setFallBackPartner
    )

    const carouselDates = getCaruselDates(
        numberOfDaysLoaded,
        loadingStartDate,
        dependencies?.service,
        date,
        dependencies?.partner
    )

    const moveBooking = async (): Promise<void> => {
        try {
            const { date, time } = BFSM.getData<MoveBookingState>()
            const moveBookingObj: IMoveBookingDTO = {
                bookingId: bookingId!,
                newServiceDate: new RRDate(
                    `${date!.toString('yyyy-MM-dd')} ${time}`,
                    'yyyy-MM-dd HH:mm'
                ).toString('yyyy-MM-dd HH:mm:ss'),
            }
            const response = await BFSM.repo.moveBooking(moveBookingObj)

            if (response == 'Success') {
                const serviceName = dependencies?.service.name || ''
                const partnerType =
                    dependencies?.basisType.id == 1
                        ? 'massør'
                        : dependencies?.basisType.id == 1
                        ? 'personal trainer'
                        : 'yoga trainer'
                const alias = dependencies?.partner?.alias || ''
                const serviceDate = `${date!.toString('yyyy-MM-dd')} ${time}`
                EFM.edit_chain.do({
                    serviceName,
                    partnerType,
                    alias,
                    serviceDate,
                })
            }
            setAntiSpam(false)
        } catch (err) {
            dispatch(setErrorcase('MoveBookFinalizeError'))
            BFSM.doAction(GeneralActions.GoToErrorState, undefined)
        }
    }

    const isBookingInThePast = (serviceDate: string): boolean => {
        return new RRDate(serviceDate).isBefore(new RRDate())
    }

    const isBookingWithin24hours = (
        serviceDate: string
    ): { within24Hours: boolean; numberOfHours: number } => {
        const diffInHours = RRDate.diff(
            new RRDate(serviceDate),
            new RRDate(),
            'hours'
        ).hours
        const isWithin24Hours = diffInHours < 24 ? true : false
        return {
            numberOfHours: parseInt(diffInHours.toString()), // we only want to show rounded numbers
            within24Hours: isWithin24Hours,
        }
    }

    const cancelBooking = async () => {
        const response = await BFSM.repo.cancelBookingCustomer(bookingId!)
        if (response === 'Success') {
            EFM.cancellation_chain.do()
        } else {
            BFSM.doAction(GeneralActions.GoToErrorState, undefined)
        }
    }

    /**
     * Function being called when in the MovebookinConfirmation Modal
     * @returns
     */
    const onClickMoveBooking = () => {
        if (antiSpam) return
        if (!antiSpam) setAntiSpam(true)
        moveBooking()
    }

    const openMoveBookModal = () => {
        setShowMoveBookingModal(true)
    }

    const onClickCancelBooking = () => {
        if (!showCancelBookingModal) {
            setShowCancelBookingModal(true)
        } else if (showCancelBookingModal) {
            setAntiSpam(true)
            cancelBooking()
        }
    }

    /**
     * If time has already been selected, deselect to remove illegal values.
     * @param date
     */
    const onDateSelection = (date: RRDate | undefined) => {
        if (time) setTime(undefined)
        setDate(date)
    }

    const onScrollEvent = (firstDate: string, lastDate: string) => {
        const daysInArray = carouselDates.length
        const indexOfCurrentLastDate = carouselDates
            .map((d) => d.date)
            .indexOf(lastDate)
        const shallLoadMore = daysInArray - 1 == indexOfCurrentLastDate
        if (shallLoadMore)
            moveBookLoadMoreDays(
                slideLength,
                dependencies,
                carouselDates,
                BFSM,
                setDependencies,
                setNumberOfDaysLoaded,
                numberOfDaysLoaded
            )
    }

    /**
     * This useEffect exists for the purpose of auto selecting a date when page is initially loaded.
     */
    useEffect(() => {
        if (
            carouselDates.length > 1 &&
            date === undefined &&
            canBookingBeMoved
        ) {
            const enabledDates = carouselDates.filter(
                (d) => d.state === 'enabled'
            )
            if (enabledDates.length > 0) {
                setDate(new RRDate(enabledDates[0].date, 'yyyy-MM-dd'))
            }
        }
    }, [carouselDates])

    useEffect(() => {
        if (!date && carouselDates.length > 0) {
            const selectedDate = carouselDates.find(
                (cDate) => cDate.state === 'selected'
            )
            if (selectedDate)
                setDate(new RRDate(selectedDate.date, 'yyyy-MM-dd'))
        }
    }, [date, carouselDates, setDate])

    useEffect(() => {
        if (oldBooking) {
            const isBookingCancelled = oldBooking.orderStatus === 'Cancelled'
            const { within24Hours, numberOfHours } = isBookingWithin24hours(
                oldBooking.serviceDate
            )
            if (isBookingCancelled) {
                dispatch(
                    setErrorcase(
                        flowType === 'MoveBooking'
                            ? 'MoveBookCancelled'
                            : 'CancelBookAlreadyCancelled'
                    )
                )
                BFSM.doAction(GeneralActions.GoToErrorState, undefined)
            } else if (isBookingInThePast(oldBooking.serviceDate)) {
                dispatch(
                    setErrorcase(
                        flowType === 'MoveBooking'
                            ? 'MoveBookInPast'
                            : 'CancelBookingInThePast'
                    )
                )
                BFSM.doAction(GeneralActions.GoToErrorState, undefined)
            } else if (within24Hours && flowType === 'MoveBooking') {
                dispatch(setErrorcase('MoveBookWithin24Hours'))
                BFSM.doAction(GeneralActions.GoToErrorState, undefined)
            } else if (within24Hours && flowType === 'CancelBooking') {
                setDependencies(undefined)
                setShouldPayCancellationFee(true)
                setNumberOfHoursUntillServiceDate(numberOfHours)
            }
        }
    }, [oldBooking, BFSM, dispatch])

    const selectedDate = useMemo(() => {
        return date && time
            ? new RRDate(date.toString('yyyy-MM-dd'), 'yyyy-MM-dd')
                  .set({ hour: parseInt(time.substring(0, 2)) })
                  .set({ minute: parseInt(time.substring(3, 5)) })
            : new RRDate()
    }, [date, time])

    const movebookModalProps: MoveBookModalProps = {
        originalServiceDate: new RRDate(oldBooking?.serviceDate),
        newTime: selectedDate,
        showModal: showMoveBookingModal,
        onClickMoveBooking: onClickMoveBooking,
        setShowModal: setShowMoveBookingModal,
    }

    const canMoveBooking = BFSM.canDo(MoveBookingActions.Next)

    if (isLoading) {
        return (
            <InitialLoading
                basisType={
                    (dependencies?.basisType as BasisTypeBF) ?? massageBasisType
                }
                elementsToShow={{
                    showCarouselLoading: true,
                    showGoalsLoading: true,
                    showPartnerCardsLoading: true,
                    showServicePickerLoading: true,
                    showServiceTypesLoading: true,
                }}
            />
        )
    }

    return (
        <ModifyBookingTemplate
            antiSpam={antiSpam}
            dependencies={dependencies}
            dateHandler={[
                date as RRDate,
                onDateSelection,
                carouselDates,
                onScrollEvent,
            ]}
            oldBooking={oldBooking}
            timeHandler={[time as string, setTime]}
            buttonsArray={[
                {
                    buttonName: 'MoveCTA',
                    onClickHandler: openMoveBookModal,
                    shouldShowButton:
                        dependencies && canBookingBeMoved ? true : false, // always show move book cta
                    isClickable: canMoveBooking,
                },
                {
                    buttonName: 'CancelBookCTA',
                    onClickHandler: onClickCancelBooking,
                    shouldShowButton: shouldShowCancelButton,
                    isClickable: !showCancelBookingModal, // always clickable if in cancelflow but NOT if Modal is opened, as 'final accept' happens there
                },
            ]}
            cancelBookingHandler={{
                numberOfHoursUntillServiceDate: numberOfHoursUntillServiceDate,
                shouldPayCancellationFee: shouldPayCancellationFee,
                showCancelBookingModal: showCancelBookingModal,
                setShowCancelBookingModal: setShowCancelBookingModal,
            }}
            flowType={flowType}
            movebookModalProps={movebookModalProps}
            fallBackPartner={fallBackPartner}
        />
    )
}
